// useAuth — обёртка над react-query для /me/ + login/logout mutations.
//
// react-query v5 single-object signature (миграция v4→v5).
// status: 'pending' | 'error' | 'success' (НЕ 'loading').
// isLoading → isPending для запросов.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMe, login, logout } from "@/api/auth";
import type { LoginPayload, MeResponse } from "@/api/types";
import { clearMultitoolKeys } from "./useLocalStorage";
import { AUTH_SIGNAL_KEY, ME_STALE_MS } from "@/api/constants";
import { resetAnnouncerClientState } from "./useAnnouncer";

export const ME_QUERY_KEY = ["auth", "me"] as const;

/** Cross-tab signal: при logout пишем timestamp в localStorage, другие вкладки
 *  ловят storage-event и реагируют (logout-out + navigate /login). */
function emitAuthSignal(): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(AUTH_SIGNAL_KEY, String(Date.now()));
  } catch {
    // QuotaExceeded / sandbox — игнорируем; локальный logout всё равно сработает.
  }
}

interface UseMeOptions {
  /** Если true — НЕ ретраить и НЕ кидать на /login при 401 (для публичных страниц). */
  optional?: boolean;
}

export function useMe(options: UseMeOptions = {}) {
  return useQuery<MeResponse | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await fetchMe();
      } catch (err) {
        // 401 — нормальный сигнал «не залогинен»; возвращаем null, чтобы
        // ProtectedRoute сделал редирект, а LoginPage — отрендерился.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401) return null;
        throw err;
      }
    },
    // 401 кидается ниже до catch, поэтому network-retry достаточен.
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
    staleTime: ME_STALE_MS,
    refetchOnWindowFocus: !options.optional,
  });
}

export function useLoginMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LoginPayload) => login(payload),
    onSuccess: () => {
      // После успешного login — форсим refetch /me/, чтобы получить актуальный
      // is_active и список tools для Sidebar.
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

/** Универсальная очистка клиентского состояния. Используется и в успешном logout,
 *  и в onError (когда сессия уже мертва на бэкенде), и в cross-tab handler'е. */
export function resetClientState(qc: ReturnType<typeof useQueryClient>): void {
  // 1. Отменяем все in-flight queries до clear() — react-query сам игнорирует
  //    их результаты, не сохраняя в кэш ушедшего юзера. (Для mutations в v5
  //    нет публичного cancelMutations — MutationCache очищается через qc.clear().)
  //    HTTP-запрос всё равно может долететь до backend (создав task), но
  //    результат не попадёт в UI следующего юзера в этой вкладке.
  void qc.cancelQueries();
  // 2. localStorage cleanup — security: на shared computer следующий юзер
  //    не должен видеть данные предыдущего.
  clearMultitoolKeys();
  // 3. Сбрасываем все кэши react-query — там могут быть приватные данные
  //    (логи, генерации, конфиги tools). Это также удаляет mutations.
  qc.clear();
  // 4. Синглтоны вне react-query (низкая r5 №8): «Анонсы для соцсетей» держат
  //    сохранённый запуск и память ключа идемпотентности МОДУЛЬНЫМИ
  //    переменными (announcerRunStore.ts, useAnnouncer.ts) — qc.clear() их не
  //    видит и не трогает. Это ЕДИНАЯ точка выхода (обычный logout и logout
  //    из соседней вкладки — useCrossTabLogout.ts — оба идут через эту
  //    функцию), поэтому сброс подключён здесь, а не размазан по инструментам.
  //    На поведение выхода для других страниц/инструментов это не влияет:
  //    вызов затрагивает только собственные синглтоны анонсера.
  resetAnnouncerClientState();
}

export function useLogoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      resetClientState(qc);
      emitAuthSignal();
    },
    onError: () => {
      // Даже при ошибке logout (например, сессия уже умерла) — чистим клиент.
      resetClientState(qc);
      emitAuthSignal();
    },
  });
}
